import React, { useEffect, useState } from 'react';
import CogginsUploadField from '../shared/CogginsUploadField.tsx';
import {
  deleteHorseCogginsDocument,
  uploadHorseCogginsDocument,
  uploadHorseImage
} from '../../utils/horseCareApi.ts';

export interface HorseCareData {
  id: string;
  name?: string;
  breed?: string;
  age?: number;
  color?: string;
  stallNumber?: string;
  status?: string;
  temperament?: string;
  ridingLevel?: string;
  acquisitionDate?: string;
  imageUrl?: string;
  farrierName?: string;
  lastSeenByFarrier?: string;
  lastCoggins?: string;
  lastWormer?: string;
  lastRabies?: string;
  specialNeeds?: string;
  medicalNotes?: string;
  notes?: string;
  cogginsDocumentUrl?: string | null;
  cogginsDocumentOriginalName?: string | null;
}

interface FamilyHorseCareEditorProps {
  horse: HorseCareData;
  onCancel: () => void;
  onSaved: (updated: Partial<HorseCareData>) => void;
}

function toDateInput(value?: string) {
  if (!value) return '';
  return value.split('T')[0];
}

const STATUS_OPTIONS = ['In herd', 'In isolation', 'coming soon', 'do not ride'] as const;

function buildFormState(horse: HorseCareData) {
  return {
    name: horse.name || '',
    breed: horse.breed || '',
    age: horse.age != null ? String(horse.age) : '',
    color: horse.color || '',
    stallNumber: horse.stallNumber || '',
    status: horse.status || 'In herd',
    temperament: horse.temperament || '',
    ridingLevel: horse.ridingLevel || '',
    acquisitionDate: toDateInput(horse.acquisitionDate),
    farrierName: horse.farrierName || '',
    lastSeenByFarrier: toDateInput(horse.lastSeenByFarrier),
    lastCoggins: toDateInput(horse.lastCoggins),
    lastWormer: toDateInput(horse.lastWormer),
    lastRabies: toDateInput(horse.lastRabies),
    specialNeeds: horse.specialNeeds || '',
    medicalNotes: horse.medicalNotes || '',
    notes: horse.notes || ''
  };
}

export default function FamilyHorseCareEditor({ horse, onCancel, onSaved }: FamilyHorseCareEditorProps) {
  const [form, setForm] = useState(() => buildFormState(horse));
  const [cogginsFile, setCogginsFile] = useState<File | null>(null);
  const [cogginsUrl, setCogginsUrl] = useState<string | null>(horse.cogginsDocumentUrl || null);
  const [cogginsName, setCogginsName] = useState<string | null>(horse.cogginsDocumentOriginalName || null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(horse.imageUrl || null);
  const [loading, setLoading] = useState(false);
  const [removeCogginsPending, setRemoveCogginsPending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(buildFormState(horse));
    setCogginsUrl(horse.cogginsDocumentUrl || null);
    setCogginsName(horse.cogginsDocumentOriginalName || null);
    setPhotoPreview(horse.imageUrl || null);
    setPhotoFile(null);
    setCogginsFile(null);
    setError('');
  }, [
    horse.id,
    horse.name,
    horse.breed,
    horse.age,
    horse.color,
    horse.stallNumber,
    horse.status,
    horse.temperament,
    horse.ridingLevel,
    horse.acquisitionDate,
    horse.farrierName,
    horse.lastSeenByFarrier,
    horse.lastCoggins,
    horse.lastWormer,
    horse.lastRabies,
    horse.specialNeeds,
    horse.medicalNotes,
    horse.notes,
    horse.imageUrl,
    horse.cogginsDocumentUrl,
    horse.cogginsDocumentOriginalName
  ]);

  const handlePhotoChange = (file: File | null) => {
    setPhotoFile(file);
    if (file) {
      setPhotoPreview(URL.createObjectURL(file));
    } else {
      setPhotoPreview(horse.imageUrl || null);
    }
  };

  const handleRemoveCoggins = async () => {
    if (!window.confirm('Remove the attached Coggins document?')) return;
    setRemoveCogginsPending(true);
    setError('');
    try {
      await deleteHorseCogginsDocument(horse.id);
      setCogginsUrl(null);
      setCogginsName(null);
      setCogginsFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove document');
    } finally {
      setRemoveCogginsPending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const payload = {
        ...form,
        age: form.age === '' ? null : parseInt(form.age, 10)
      };
      const response = await fetch(`/api/horses/${horse.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to save');
      }

      let nextImageUrl = photoPreview;
      if (photoFile) {
        const imageResult = await uploadHorseImage(horse.id, photoFile);
        nextImageUrl = imageResult.imageUrl || nextImageUrl;
      }

      let nextCogginsUrl = cogginsUrl;
      let nextCogginsName = cogginsName;
      if (cogginsFile) {
        const uploadResult = await uploadHorseCogginsDocument(horse.id, cogginsFile);
        nextCogginsUrl = uploadResult.data?.cogginsDocumentUrl || nextCogginsUrl;
        nextCogginsName = uploadResult.data?.cogginsDocumentOriginalName || cogginsFile.name;
      }

      onSaved({
        ...form,
        age: payload.age ?? undefined,
        imageUrl: nextImageUrl || undefined,
        cogginsDocumentUrl: nextCogginsUrl,
        cogginsDocumentOriginalName: nextCogginsName
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save horse details');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    'w-full border border-gray-300 rounded-lg px-3 py-3 sm:py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-ggaa-green min-h-[44px]';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Horse profile</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Breed</label>
            <input
              type="text"
              value={form.breed}
              onChange={(e) => setForm({ ...form, breed: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Age (years)</label>
            <input
              type="number"
              min={0}
              value={form.age}
              onChange={(e) => setForm({ ...form, age: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <input
              type="text"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stall number</label>
            <input
              type="text"
              value={form.stallNumber}
              onChange={(e) => setForm({ ...form, stallNumber: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className={fieldClass}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temperament</label>
            <input
              type="text"
              value={form.temperament}
              onChange={(e) => setForm({ ...form, temperament: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Riding level</label>
            <input
              type="text"
              value={form.ridingLevel}
              onChange={(e) => setForm({ ...form, ridingLevel: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Acquisition date</label>
            <input
              type="date"
              value={form.acquisitionDate}
              onChange={(e) => setForm({ ...form, acquisitionDate: e.target.value })}
              className={fieldClass}
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Horse photo</label>
          {photoPreview && (
            <img src={photoPreview} alt="" className="w-24 h-24 rounded-lg object-cover border border-gray-200 mb-2" />
          )}
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
            disabled={loading}
            onChange={(e) => handlePhotoChange(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-ggaa-green file:text-white"
          />
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Farrier & health</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farrier name</label>
            <input
              type="text"
              value={form.farrierName}
              onChange={(e) => setForm({ ...form, farrierName: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last seen by farrier</label>
            <input
              type="date"
              value={form.lastSeenByFarrier}
              onChange={(e) => setForm({ ...form, lastSeenByFarrier: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Coggins</label>
            <input
              type="date"
              value={form.lastCoggins}
              onChange={(e) => setForm({ ...form, lastCoggins: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last wormer</label>
            <input
              type="date"
              value={form.lastWormer}
              onChange={(e) => setForm({ ...form, lastWormer: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last rabies</label>
            <input
              type="date"
              value={form.lastRabies}
              onChange={(e) => setForm({ ...form, lastRabies: e.target.value })}
              className={fieldClass}
            />
          </div>
        </div>

        <div className="mt-4">
          <CogginsUploadField
            horseId={horse.id}
            existingUrl={cogginsUrl}
            existingFileName={cogginsName}
            selectedFile={cogginsFile}
            onFileChange={setCogginsFile}
            onRemoveExisting={cogginsUrl ? handleRemoveCoggins : undefined}
            removeExistingPending={removeCogginsPending}
            disabled={loading}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Special needs</label>
          <textarea
            value={form.specialNeeds}
            onChange={(e) => setForm({ ...form, specialNeeds: e.target.value })}
            rows={3}
            className={`${fieldClass} min-h-[88px]`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Medical notes</label>
          <textarea
            value={form.medicalNotes}
            onChange={(e) => setForm({ ...form, medicalNotes: e.target.value })}
            rows={3}
            className={`${fieldClass} min-h-[88px]`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            className={`${fieldClass} min-h-[72px]`}
          />
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="w-full sm:w-auto px-4 py-3 rounded-lg border border-gray-300 text-gray-700 min-h-[44px]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="w-full sm:flex-1 bg-ggaa-green text-white px-4 py-3 rounded-lg font-medium min-h-[44px] disabled:opacity-50"
        >
          {loading ? 'Saving…' : 'Save horse details'}
        </button>
      </div>
    </form>
  );
}
